import React, { useState, useEffect, useCallback } from 'react';
import config from '../config';
import '../styles/TrackerPage.css';

const STATUS_OPTIONS = ['Sent', 'Pending', 'Assigned', 'Follow Up Sent', 'Completed'];
const STATUS_COLORS = {
  Sent:             { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
  Pending:          { bg: '#fefce8', text: '#a16207', border: '#fde68a' },
  Assigned:         { bg: '#f5f3ff', text: '#6d28d9', border: '#ddd6fe' },
  'Follow Up Sent': { bg: '#fff7ed', text: '#c2410c', border: '#fed7aa' },
  Completed:        { bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0' },
};

// ═══════════════════════════════════════════════════════════════
// Dashboard Cards
// ═══════════════════════════════════════════════════════════════

const DashboardCards = ({ authToken }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${config.apiBaseUrl}/api/share-tracker/dashboard`, {
          headers: { 'Authorization': `Bearer ${authToken}` },
        });
        const d = await res.json();
        if (d.success) setData(d.dashboard);
      } catch (e) { console.error('Dashboard error:', e); }
    })();
  }, [authToken]);

  if (!data) return null;

  const cards = [
    { label: 'Total Sent', value: data.total_sent, icon: '📤', color: '#3b82f6' },
    { label: 'Pending Action', value: data.pending_action, icon: '⏳', color: '#f59e0b' },
    { label: 'Completed', value: data.completed, icon: '✅', color: '#10b981' },
    { label: 'Opened', value: data.opened, icon: '👁️', color: '#8b5cf6' },
  ];

  return (
    <div className="tp-dashboard">
      {cards.map((c, i) => (
        <div key={i} className="tp-card" style={{ borderLeftColor: c.color }}>
          <span className="tp-card-icon">{c.icon}</span>
          <div>
            <span className="tp-card-value">{c.value}</span>
            <div className="tp-card-label">{c.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Smart Nudges
// ═══════════════════════════════════════════════════════════════

const SmartNudges = ({ authToken }) => {
  const [nudges, setNudges] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${config.apiBaseUrl}/api/share-tracker/nudges`, {
          headers: { 'Authorization': `Bearer ${authToken}` },
        });
        const d = await res.json();
        if (d.success && d.nudges?.length) setNudges(d.nudges);
      } catch (e) { console.error('Nudges error:', e); }
    })();
  }, [authToken]);

  if (nudges.length === 0) return null;

  const severityStyles = {
    warning: { bg: '#fffbeb', border: '#fde68a', icon: '⚠️' },
    info:    { bg: '#eff6ff', border: '#bfdbfe', icon: '💡' },
    success: { bg: '#f0fdf4', border: '#bbf7d0', icon: '🎉' },
  };

  return (
    <div className="tp-nudges">
      {nudges.map((n, i) => {
        const s = severityStyles[n.severity] || severityStyles.info;
        return (
          <div key={i} className="tp-nudge" style={{ background: s.bg, borderColor: s.border }}>
            <span className="tp-nudge-icon">{s.icon}</span>
            <div className="tp-nudge-content">
              <span className="tp-nudge-title">{n.title}</span>
              <span className="tp-nudge-msg">{n.message}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Status Timeline
// ═══════════════════════════════════════════════════════════════

const StatusTimeline = ({ history }) => {
  if (!history || history.length === 0) return null;

  return (
    <div className="tp-timeline">
      {history.map((h, i) => (
        <div key={i} className="tp-timeline-item">
          <div className="tp-timeline-dot" />
          {i < history.length - 1 && <div className="tp-timeline-line" />}
          <div className="tp-timeline-info">
            <span className="tp-timeline-status">{h.status}</span>
            <span className="tp-timeline-time">{formatDate(h.changed_at)}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Helper
// ═══════════════════════════════════════════════════════════════

function formatDate(isoStr) {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  return d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function truncate(str, n = 50) {
  if (!str) return '—';
  return str.length > n ? str.substring(0, n) + '…' : str;
}

// ═══════════════════════════════════════════════════════════════
// TrackerPage
// ═══════════════════════════════════════════════════════════════

const TrackerPage = ({ authToken, onClose }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resending, setResending] = useState(null);

  // ◀── Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(20);

  // ◀── PDF download state
  const [downloading, setDownloading] = useState(null);

  const fetchTickets = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(
        `${config.apiBaseUrl}/api/share-tracker/list?page=${currentPage}&page_size=${pageSize}`,
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      const data = await res.json();
      if (data.success) {
        setTickets(data.tickets || []);
        setTotal(data.total || 0);
        setTotalPages(data.total_pages || 1);
      }
      else setError(data.error || 'Failed to load');
    } catch (err) { setError('Network error'); }
    finally { setLoading(false); }
  }, [authToken, currentPage, pageSize]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  // ◀── Pagination handlers
  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setExpandedId(null);
    setExpandedDetail(null);
    setCurrentPage(page);
  };

  // ◀── PDF download handler
  const handleDownloadPdf = async (trackerId) => {
    setDownloading(trackerId);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}/api/share-tracker/${trackerId}/download-pdf`,
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      const data = await res.json();
      if (data.success && data.download_url) {
        window.open(data.download_url, '_blank');
      } else {
        alert(data.error || 'PDF not available for this email');
      }
    } catch (err) {
      console.error('PDF download error:', err);
      alert('Failed to download PDF');
    } finally {
      setDownloading(null);
    }
  };

  const updateStatus = async (trackerId, newStatus) => {
    try {
      const res = await fetch(`${config.apiBaseUrl}/api/share-tracker/${trackerId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (data.success) {
        setTickets(prev => prev.map(t => t.tracker_id === trackerId
          ? { ...t, status: newStatus, updated_at: new Date().toISOString() } : t
        ));
      }
    } catch (err) { console.error('Status update error:', err); }
  };

  const handleResend = async (trackerId) => {
    setResending(trackerId);
    try {
      const res = await fetch(`${config.apiBaseUrl}/api/share/resend/${trackerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.success) {
        setTickets(prev => prev.map(t => t.tracker_id === trackerId
          ? { ...t, resend_count: (t.resend_count || 0) + 1 } : t
        ));
      }
    } catch (err) { console.error('Resend error:', err); }
    finally { setResending(null); }
  };

  const toggleDetail = async (trackerId) => {
    if (expandedId === trackerId) { setExpandedId(null); setExpandedDetail(null); return; }
    setExpandedId(trackerId); setDetailLoading(true);
    try {
      const res = await fetch(`${config.apiBaseUrl}/api/share-tracker/${trackerId}`, {
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      const data = await res.json();
      if (data.success) setExpandedDetail(data.ticket);
    } catch (err) { console.error('Detail error:', err); }
    finally { setDetailLoading(false); }
  };

  return (
    <div className="tp-page">
      {/* Header */}
      <div className="tp-header">
        <div className="tp-header-left">
          <button className="tp-back" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <h2 className="tp-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 6L2 7" />
            </svg>
            Email Tracker
          </h2>
          <span className="tp-count">{total}</span>
        </div>
        <button className="tp-refresh" onClick={fetchTickets} disabled={loading}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            className={loading ? 'tp-spin' : ''}>
            <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
        </button>
      </div>

      <div className="tp-content">
        {/* Dashboard */}
        <DashboardCards authToken={authToken} />

        {/* Nudges */}
        <SmartNudges authToken={authToken} />

        {/* Content area */}
        {loading && tickets.length === 0 ? (
          <div className="tp-loading"><div className="tp-loader" /><p>Loading tracker…</p></div>
        ) : error ? (
          <div className="tp-error"><span>⚠️</span> {error} <button onClick={fetchTickets}>Retry</button></div>
        ) : tickets.length === 0 ? (
          <div className="tp-empty">
            <div className="tp-empty-icon">📧</div>
            <p className="tp-empty-title">No emails shared yet</p>
            <p className="tp-empty-sub">Use the share button on any AI response to send it via email.</p>
          </div>
        ) : (
          <div className="tp-table-wrap">
            <table className="tp-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>To</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Tracking</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => {
                  const isExpanded = expandedId === ticket.tracker_id;
                  const sc = STATUS_COLORS[ticket.status] || STATUS_COLORS.Sent;
                  const isResendingThis = resending === ticket.tracker_id;

                  return (
                    <React.Fragment key={ticket.tracker_id}>
                      <tr className={isExpanded ? 'tp-row--expanded' : ''}>
                        <td className="tp-cell-date">{formatDate(ticket.sent_at)}</td>
                        <td className="tp-cell-to" title={(ticket.to || []).join(', ')}>
                          {(ticket.to || []).join(', ')}
                        </td>
                        <td className="tp-cell-subject" title={ticket.subject}>
                          {truncate(ticket.subject, 55)}
                        </td>
                        <td>
                          {ticket.status === 'Completed' ? (
                            <span className="tp-status-select" style={{
                              background: sc.bg, color: sc.text, borderColor: sc.border,
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                              padding: '4px 10px', borderRadius: '6px', border: `1px solid ${sc.border}`,
                              fontSize: '13px', fontWeight: 500, cursor: 'default',
                            }}>
                              ✅ Completed
                            </span>
                          ) : (
                            <select className="tp-status-select" value={ticket.status}
                              onChange={(e) => updateStatus(ticket.tracker_id, e.target.value)}
                              style={{ background: sc.bg, color: sc.text, borderColor: sc.border }}>
                              {STATUS_OPTIONS.filter(s => s !== 'Completed' || ticket.status === 'Completed')
                                .map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          )}
                        </td>
                        <td className="tp-cell-tracking">
                          {ticket.open_count > 0 ? (
                            <span className="tp-opened-badge" title={`Opened ${ticket.open_count} time(s)${ticket.opened_at ? ' — last: ' + formatDate(ticket.opened_at) : ''}`}>
                              👁️ {ticket.open_count}
                            </span>
                          ) : (
                            <span className="tp-not-opened">—</span>
                          )}
                        </td>
                        <td className="tp-cell-actions">
                          {/* PDF download — only when S3 file exists */}
                          {ticket.s3_key ? (
                            <button className="tp-action-btn" onClick={() => handleDownloadPdf(ticket.tracker_id)}
                              disabled={downloading === ticket.tracker_id} title="Download PDF">
                              {downloading === ticket.tracker_id ? <span className="tp-mini-spin" /> : '📥'}
                            </button>
                          ) : null}
                          <button className="tp-action-btn" onClick={() => handleResend(ticket.tracker_id)}
                            disabled={isResendingThis} title="Re-send email">
                            {isResendingThis ? <span className="tp-mini-spin" /> : '🔄'}
                          </button>
                          <button className={`tp-action-btn ${isExpanded ? 'tp-action-btn--active' : ''}`}
                            onClick={() => toggleDetail(ticket.tracker_id)} title="View details">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </button>
                        </td>
                      </tr>

                      {/* Expanded detail */}
                      {isExpanded && (
                        <tr className="tp-detail-row">
                          <td colSpan={6}>
                            {detailLoading ? (
                              <div className="tp-detail-loading">Loading…</div>
                            ) : expandedDetail ? (
                              <div className="tp-detail">
                                <div className="tp-detail-cols">
                                  {/* Left: info */}
                                  <div className="tp-detail-info">
                                    <div className="tp-detail-item">
                                      <span className="tp-detail-label">CC</span>
                                      <span className="tp-detail-value">{(expandedDetail.cc || []).join(', ') || '—'}</span>
                                    </div>
                                    <div className="tp-detail-item">
                                      <span className="tp-detail-label">From</span>
                                      <span className="tp-detail-value">{expandedDetail.from_email || 'System'}</span>
                                    </div>
                                    <div className="tp-detail-item">
                                      <span className="tp-detail-label">Re-sends</span>
                                      <span className="tp-detail-value">{expandedDetail.resend_count || 0}</span>
                                    </div>
                                    <div className="tp-detail-item">
                                      <span className="tp-detail-label">Tracker ID</span>
                                      <span className="tp-detail-value tp-detail-mono">{expandedDetail.tracker_id}</span>
                                    </div>

                                    {expandedDetail.body && (
                                      <div className="tp-detail-section">
                                        <span className="tp-detail-label">Message Body</span>
                                        <div className="tp-detail-text">{expandedDetail.body}</div>
                                      </div>
                                    )}

                                    {expandedDetail.message_content?.question && (
                                      <div className="tp-detail-section">
                                        <span className="tp-detail-label">Original Question</span>
                                        <div className="tp-detail-text tp-detail-question">
                                          {expandedDetail.message_content.question}
                                        </div>
                                      </div>
                                    )}

                                    {expandedDetail.message_content?.response && (
                                      <div className="tp-detail-section">
                                        <span className="tp-detail-label">AI Response (preview)</span>
                                        <div className="tp-detail-text">
                                          {truncate(expandedDetail.message_content.response, 300)}
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  {/* Right: status timeline */}
                                  <div className="tp-detail-timeline-col">
                                    <span className="tp-detail-label">Status Timeline</span>
                                    <StatusTimeline history={expandedDetail.status_history} />
                                  </div>
                                </div>
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {/* ◀── Pagination Controls */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '8px', padding: '16px 20px', borderTop: '1px solid #e5e7eb',
                background: '#fafafa', borderRadius: '0 0 8px 8px',
              }}>
                <button
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1 || loading}
                  title="First page"
                  style={{
                    padding: '6px 10px', border: '1px solid #d0d5dd', borderRadius: '6px',
                    background: '#fff', color: '#344054', fontSize: '13px', cursor: 'pointer',
                    opacity: currentPage === 1 ? 0.4 : 1,
                  }}
                >
                  ««
                </button>
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  title="Previous page"
                  style={{
                    padding: '6px 14px', border: '1px solid #d0d5dd', borderRadius: '6px',
                    background: '#fff', color: '#344054', fontSize: '13px', fontWeight: 500,
                    cursor: 'pointer', opacity: currentPage === 1 ? 0.4 : 1,
                  }}
                >
                  ‹ Prev
                </button>

                <span style={{
                  padding: '0 12px', fontSize: '13px', color: '#374151', fontWeight: 500,
                }}>
                  Page {currentPage} of {totalPages}
                  <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: '6px' }}>
                    ({total} emails)
                  </span>
                </span>

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                  title="Next page"
                  style={{
                    padding: '6px 14px', border: '1px solid #d0d5dd', borderRadius: '6px',
                    background: '#fff', color: '#344054', fontSize: '13px', fontWeight: 500,
                    cursor: 'pointer', opacity: currentPage === totalPages ? 0.4 : 1,
                  }}
                >
                  Next ›
                </button>
                <button
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages || loading}
                  title="Last page"
                  style={{
                    padding: '6px 10px', border: '1px solid #d0d5dd', borderRadius: '6px',
                    background: '#fff', color: '#344054', fontSize: '13px', cursor: 'pointer',
                    opacity: currentPage === totalPages ? 0.4 : 1,
                  }}
                >
                  »»
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackerPage;
